import React, { useEffect, useRef, useState } from 'react'
import Compressor from 'compressorjs'
import {
  COVER_TYPE_PHOTO,
  COVER_TYPE_VIDEO,
  MAX_UPLOAD_IMAGE_SIZE,
  MAX_UPLOAD_VIDEO_SIZE,
} from '@/constants'
import { Colors, Gradation } from '@/constants/styles/color'
import { Covers, Resource } from '@/types/Creator.type'
import { IMAGE_COMPRESS_SIZE } from '@/constants/length'

import ImageEdit from '@/assets/img/icons/ImageEdit.svg'

import FanmeLoading from '@/components/atoms/FanmeLoading'
import Dialog from '@/components/atoms/PopUp'
import HeadLine from '@/components/atoms/HeadLine'
import TrimmingCoverImageContainer from '@/components/molecules/TrimmingCoverImage'
import AvatarNotification from '@/components/molecules/Notification/ProfileNotification'
import GradationSwitch from '@/components/molecules/GradationSwitch'

import { useToast } from '@/contexts/ToastContext'
import { putForm } from '@/pages/api/base'
import { updateCover } from '@/pages/api/creator'
import ToggleSwitch from '@/components/atoms/ToggleSwitch'
import { useThemeContext } from '@/contexts/ThemeContext'
import AlertDialog from '@/components/molecules/Alert/alert'
import styles from '../style.module.scss'

interface Props {
  covers: Covers
  getCoverImage: () => void
}

const CreatorCoverType = ({ covers, getCoverImage }: Props) => {
  // テーマ
  const { isDark, themeColors } = useThemeContext()

  const [isLoading, setIsLoading] = useState(false)
  const [popUpVisible, setPopUpVisible] = useState(false)

  // カバー画像/動画
  const [currentCoverImg, setCurrentCoverImg] = useState<any>(null)
  const [coverImage, setCoverImage] = useState('')
  const [coverImgCanvas, setCoverImgCanvas] = useState<HTMLCanvasElement>()
  const coverImageRef = useRef<HTMLInputElement>(null)
  // カバー画像/動画の表示
  const [coverNotifyText, setCoverNotifyText] = useState<string>('')
  const [coverDisplayable, setCoverDisplayable] = useState<boolean>(false)
  const onClickUploadImage = () => {
    if (coverImageRef.current) {
      coverImageRef.current.click()
    }
  }
  // カバー画像/動画のトリミング
  const [trimImageModal, setTrimImageModal] = useState<boolean>(false)
  // カバー画像内容超過のアラート
  const [alertDialogVisible, setAlertDialogVisible] = useState(false)

  // Mediaタイプ
  const [coverType, setCoverType] = useState('')

  // グラデーション選択
  const [gradation, setGradation] = useState<string>(Gradation.BK_50)
  /**
   * グラデーションの濃さを設定
   * @param brightness グラデーションの濃さ
   */
  const updateGradation = async (brightness: string) => {
    switch (brightness) {
      case 'light':
        setGradation(Gradation.BK_25)
        break
      case 'normal':
        setGradation(Gradation.BK_50)
        break
      case 'dark':
        setGradation(Gradation.BK_75)
        break
      default:
        setGradation(Gradation.BK_25)
    }

    const form = new FormData()
    form.append('brightness', brightness)
    try {
      const response = await putForm('/creators/self/profile/covers', form)
      if (response.status === 200) {
        setGradationVisibleNotification(true)
        setTimeout(() => {
          setGradationVisibleNotification(false)
        }, 1500)
      }
    } catch (e) {
      // TODO Error handling
    }
  }

  const cancel = () => {
    const inputCoverImageElem = document.getElementById('coverImage') as HTMLInputElement
    if (inputCoverImageElem) inputCoverImageElem.value = ''
    setCoverImage(currentCoverImg)
    setTrimImageModal(false)
  }

  useEffect(() => {
    if (covers) {
      // グラデーションの設定
      const { brightness, cover_visibility } = covers
      switch (brightness) {
        case 'light':
          setGradation(Gradation.BK_25)
          break
        case 'normal':
          setGradation(Gradation.BK_50)
          break
        case 'dark':
          setGradation(Gradation.BK_75)
          break
      }
      // coverの表示・非表示
      setCoverDisplayable(Boolean(cover_visibility))
      // 画像
      const findImages = covers.resources?.filter((item: Resource) => item.displayable)
      if (findImages && findImages.length > 0) {
        const target = findImages.pop()
        if (target) {
          setCoverType(target.resource_type)
          setCoverImage(target.resource)
          setCurrentCoverImg(target.resource)
        }
      }
    }
  }, [covers])

  useEffect(() => {
    if (coverImgCanvas) {
      setCoverImage(coverImgCanvas.toDataURL())
      setCurrentCoverImg(coverImgCanvas.toDataURL())
    }
  }, [coverImgCanvas])

  const uploadCoverVideo = async (file: File) => {
    setIsLoading(true)

    const form = new FormData()
    form.append('displayable', 'true')
    form.append('resource_type', coverType)
    form.append('resource', file)

    try {
      // resource_type = videoがあるかチェック
      const isExistVideoType =
        covers.resources.filter(r => r.resource_type === COVER_TYPE_VIDEO).length > 0
      const response = await updateCover(form, COVER_TYPE_VIDEO, isExistVideoType)
      if (response.status == 200 || response.status == 201) {
        getCoverImage()
        coverImageToast.setVisible(true)
        setTimeout(() => {
          coverImageToast.setVisible(false)
        }, 1500)
      }
    } catch (e) {
      // TODO Error handling
    } finally {
      setIsLoading(false)
    }
  }

  const uploadCoverImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // ファイル読み込み
    const reader = new FileReader()
    const files = e.currentTarget.files
    if (e.target.value && files !== null) {
      const file = files[0]

      //if image filesize is larger than 32MB, show error message
      if (!!files && files[0].type.match(/^image\/.*/) && file.size > MAX_UPLOAD_IMAGE_SIZE) {
        setCoverImage(currentCoverImg)
        setAlertDialogVisible(true)
        e.target.value = ''
        return
      }

      //if video filesize is larger than 50MB, show error message
      if (!!files && files[0].type.match(/^video\/.*/) && file.size > MAX_UPLOAD_VIDEO_SIZE) {
        setIsLoading(false)
        setCoverImage(currentCoverImg)
        setPopUpVisible(true)
        e.target.value = ''
        return
      }

      // if the image is larger than 1MB, compress the image
      else if (file.size > IMAGE_COMPRESS_SIZE && files[0].type.match(/^image\/.*/)) {
        new Compressor(file, {
          quality: 0.4,
          success(result) {
            reader.readAsDataURL(result)
          },
          error(err) {
            console.log(err.message)
          },
        })
      } else {
        await reader.readAsDataURL(file)
      }
    }
    reader.onload = (readerEvent: ProgressEvent) => {
      if (readerEvent.target !== null) {
        setCoverImage((readerEvent.target as FileReader).result as string)
        if (!!files && files[0].type.match(/^image\/.*/)) {
          setCoverType(COVER_TYPE_PHOTO)
          setTrimImageModal(true)
        } else if (!!files && files[0].type.match(/^video\/.*/)) {
          setCoverType(COVER_TYPE_VIDEO)
          uploadCoverVideo(files[0])
        }
      }
    }
  }

  const handleNotification = (message: string) => {
    setCoverNotifyText(message)
    setCoverVisibleNotification(true)
    setTimeout(() => {
      setCoverVisibleNotification(false)
    }, 1500)
  }

  const onCoverToggleSwitch = (e: any) => {
    const message = e.target.checked
      ? 'カバー画像/動画を公開しました'
      : 'カバー画像/動画を非公開にしました'
    handleNotification(message)
    updateCoverFlag(e.target.checked)
  }

  const updateCoverFlag = async (flag: boolean) => {
    const form = new FormData()
    form.append('cover_visibility', flag.toString())
    try {
      const response = await putForm('/creators/self/profile/covers', form)
      if (response.status === 200) {
        setCoverDisplayable(flag)
      }
    } catch (e) {
      // TODO Error handling
    }
  }

  // Notification(カバー画像/動画)の表示・非表示
  const { coverImageToast } = useToast()
  // Notification(グラデーション)の表示・非表示
  const [gradationVisibleNotification, setGradationVisibleNotification] = useState<boolean>(false)
  // Notification(カバー画像/動画)の表示・非表示
  const [coverVisibleNotification, setCoverVisibleNotification] = useState<boolean>(false)

  const renderContent = () => (
    <div className={styles.imageIcon} onClick={onClickUploadImage}>
      {!coverImage ? (
        <div className={styles.ImageEditLarge}>
          <ImageEdit
            style={{
              width: '35px',
              height: '35px',
              zIndex: 10,
            }}
          />
        </div>
      ) : (
        <div className={styles.ImageEditLargeBK}>
          <ImageEdit
            style={{
              width: '35px',
              height: '35px',
              filter: 'drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.1))',
              fill: Colors.WHITE,
              zIndex: 10,
            }}
          />
        </div>
      )}
      <input
        id="coverImage ####"
        ref={coverImageRef}
        onChange={uploadCoverImage}
        type="file"
        accept=".png, .jpeg, .jpg, .mp4, .m4v, .mov, .mpeg4"
        hidden
      />
    </div>
  )
  const renderImage = () => (
    <div
      className={styles.leftCard}
      style={{
        backgroundImage: coverImage ? `url(${coverImage})` : ``,
        backgroundColor: !coverImage ? Colors.BK_GRAY : '',
        backgroundSize: coverImage ? 'cover' : 'auto',
        backgroundPosition: coverImage ? 'center' : '',
        filter: !coverDisplayable ? 'grayscale(60%) contrast(50%)' : '',
      }}
    >
      {renderContent()}
    </div>
  )

  const renderVideo = () => (
    <div className={styles.leftCard} style={{ position: 'relative' }}>
      <video
        src={coverImage}
        muted
        autoPlay
        loop
        playsInline
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundPosition: coverImage ? 'center' : 'auto',
          filter: !coverDisplayable ? 'grayscale(100%) contrast(50%)' : '',
          background: Colors.BLACK,
        }}
      />
      {renderContent()}
    </div>
  )

  return (
    <div className={styles.profileBlock}>
      {isLoading && <FanmeLoading />}

      {popUpVisible && (
        <AlertDialog
          visible={popUpVisible}
          message={`ファイルサイズが${MAX_UPLOAD_VIDEO_SIZE / 1000 / 1000}MBを超えています。`}
          action={() => setPopUpVisible(false)}
          close={() => setPopUpVisible(false)}
          actionMessage={'閉じる'}
        />
      )}

      <div className={styles.profileBlockItem}>
        <div className={styles.blockTitleField}>
          <HeadLine text="カバー画像/動画" textColor={themeColors.text}>
            <div className={styles.switchContainer}>
              <div className={styles.switchTitle} style={{ color: themeColors.text }}>
                OFF/ON
              </div>
              <ToggleSwitch size="sm" checked={coverDisplayable} onChange={onCoverToggleSwitch} />
            </div>
          </HeadLine>
          {coverVisibleNotification && <AvatarNotification text={coverNotifyText} top="50px" />}
        </div>

        <div className={styles.coverTypeBase}>
          <div className={styles.cardBase}>
            <div className={styles.coverImages}>
              {coverType === COVER_TYPE_PHOTO ? renderImage() : renderVideo()}
              <div className={styles.note}>* 縦型推奨</div>
            </div>
          </div>
        </div>

        {alertDialogVisible && (
          <AlertDialog
            visible={alertDialogVisible}
            message="ファイルサイズが32MBを超えています。"
            action={() => setAlertDialogVisible(false)}
            close={() => setAlertDialogVisible(false)}
            actionMessage={'閉じる'}
          />
        )}

        {coverImageToast.visible && (
          <AvatarNotification text={'カバー画像/動画を設定しました'} top="20px" />
        )}
      </div>

      <div className={styles.profileBlockItem}>
        <div className={styles.blockTitleField}>
          <HeadLine text="カバーグラデーション濃度" textColor={themeColors.text} />
        </div>
        <GradationSwitch setGradation={updateGradation} gradation={gradation} />
        {gradationVisibleNotification && (
          <AvatarNotification text={'グラデーションの濃さを設定しました'} top="-40px" />
        )}
      </div>

      {trimImageModal && (
        <TrimmingCoverImageContainer
          img={coverImage}
          setTrimModal={setTrimImageModal}
          setSelectedCoverImage={setCoverImgCanvas}
          isCoverImages={
            covers.resources.filter(r => r.resource_type === COVER_TYPE_PHOTO).length > 0
          }
          cancel={cancel}
          getCoverImage={getCoverImage}
        />
      )}
    </div>
  )
}

export default CreatorCoverType
